import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../hooks/useIsMobile';

// Guide sections mirror the sidebar's own grouping, so a reader can find a
// feature's write-up the same way they'd find it in the nav. Content/images
// sourced from the internal "Huong_dan_su_dung_ai_trustflow_vn.docx" —
// screenshots copied into public/guide/ (see git history for the mapping
// from the doc's embedded media filenames).
const SECTIONS = [
  { id: 'overview', icon: '👋' },
  { id: 'technical', icon: '📈' },
  { id: 'fundraising', icon: '💰' },
  { id: 'research', icon: '📊' },
  { id: 'personnel', icon: '👤' },
  { id: 'onchainOverview', icon: '⛓️' },
  { id: 'staking', icon: '🥩' },
  { id: 'flowGraph', icon: '🕸️' },
  { id: 'nearFlow', icon: '🔗' },
  { id: 'asterMonitor', icon: '🚀' },
  { id: 'hyperliquidMonitor', icon: '⚡' },
  { id: 'contractAudit', icon: '🛡️' },
  { id: 'etfFlow', icon: '💵' },
  { id: 'dominance', icon: '🌐' },
  { id: 'portfolio', icon: '💼' },
  { id: 'knowledge', icon: '🧠' },
  { id: 'tips', icon: '💡' },
  { id: 'conclusion', icon: '✅' },
];

export default function GuidePage() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [openId, setOpenId] = useState('overview');
  const pageStyle = isMobile ? { ...s.page, padding: '16px 12px' } : s.page;

  return (
    <div style={pageStyle}>
      <div style={s.header}>
        <h2 style={s.title}>❓ {t('guide.title')}</h2>
        <p style={s.subtitle}>{t('guide.subtitle')}</p>
      </div>

      <div style={s.layout}>
        <nav style={isMobile ? s.tocMobile : s.toc}>
          {SECTIONS.map((sec) => (
            <button
              key={sec.id}
              type="button"
              style={openId === sec.id ? s.tocItemActive : s.tocItem}
              onClick={() => setOpenId(sec.id)}
            >
              {sec.icon} {t(`guide.${sec.id}.title`)}
            </button>
          ))}
        </nav>

        <div style={s.content}>
          {SECTIONS.map((sec) => (
            <GuideSection key={sec.id} id={sec.id} icon={sec.icon} open={openId === sec.id} onToggle={() => setOpenId(openId === sec.id ? null : sec.id)} t={t} />
          ))}
        </div>
      </div>
    </div>
  );
}

// A block is one of:
//   { p: "paragraph text" }
//   { h: "subsection heading" }
//   { li: ["item 1", "item 2", ...] }        — bullet list
//   { img: "6-2a-staking-overview.jpg", caption: "Hình 6.2a — ..." }
// Legacy plain-string bodies (old `guide.*.body` shape) still render as
// paragraphs, so nothing breaks if a section hasn't been migrated yet.
function GuideSection({ id, icon, open, onToggle, t }) {
  const body = t(`guide.${id}.body`, { returnObjects: true });
  const blocks = Array.isArray(body) ? body : [body];
  return (
    <div id={`guide-${id}`} style={s.section}>
      <button type="button" style={s.sectionHead} onClick={onToggle}>
        <span style={s.sectionTitle}>{icon} {t(`guide.${id}.title`)}</span>
        <span style={s.sectionCaret}>{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div style={s.sectionBody}>
          {blocks.map((block, i) => {
            if (typeof block === 'string') return <p key={i} style={s.paragraph}>{block}</p>;
            if (block.h) return <h4 key={i} style={s.subHeading}>{block.h}</h4>;
            if (block.li) {
              return (
                <ul key={i} style={s.list}>
                  {block.li.map((item, j) => <li key={j} style={s.listItem}>{item}</li>)}
                </ul>
              );
            }
            if (block.img) {
              return (
                <figure key={i} style={s.figure}>
                  <img src={`/guide/${block.img}`} alt={block.caption || ''} style={s.figureImg} loading="lazy" />
                  {block.caption && <figcaption style={s.figureCaption}>{block.caption}</figcaption>}
                </figure>
              );
            }
            if (block.p) return <p key={i} style={s.paragraph}>{block.p}</p>;
            return null;
          })}
        </div>
      )}
    </div>
  );
}

const s = {
  page: { padding: '24px 32px', maxWidth: 1100, margin: '0 auto' },
  header: { marginBottom: 16 },
  title: { margin: '0 0 4px', fontSize: 20, fontWeight: 800, color: 'var(--text)' },
  subtitle: { margin: 0, fontSize: 13, color: 'var(--text-muted)' },

  layout: { display: 'flex', gap: 20, alignItems: 'flex-start' },
  toc: {
    flexShrink: 0, width: 220, display: 'flex', flexDirection: 'column', gap: 2,
    position: 'sticky', top: 20, background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 12, padding: 8,
  },
  tocMobile: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 },
  tocItem: {
    textAlign: 'left', fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)',
    background: 'transparent', border: 'none', borderRadius: 8, padding: '8px 10px', cursor: 'pointer',
  },
  tocItemActive: {
    textAlign: 'left', fontSize: 12.5, fontWeight: 700, color: '#fff',
    background: 'var(--accent)', border: 'none', borderRadius: 8, padding: '8px 10px', cursor: 'pointer',
  },

  content: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 },
  section: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' },
  sectionHead: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%',
    background: 'transparent', border: 'none', padding: '14px 16px', cursor: 'pointer', textAlign: 'left', font: 'inherit',
  },
  sectionTitle: { fontSize: 14.5, fontWeight: 800, color: 'var(--text)' },
  sectionCaret: { color: 'var(--text-dim)', fontSize: 13 },
  sectionBody: { borderTop: '1px solid var(--border)', padding: '12px 16px 16px' },
  paragraph: { margin: '0 0 10px', fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.7 },
  subHeading: { margin: '16px 0 8px', fontSize: 13.5, fontWeight: 800, color: 'var(--text)' },
  list: { margin: '0 0 10px', paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 4 },
  listItem: { fontSize: 13, color: 'var(--text-dim)', lineHeight: 1.6 },

  figure: { margin: '0 0 16px' },
  figureImg: { width: '100%', borderRadius: 10, border: '1px solid var(--border)', display: 'block' },
  figureCaption: { fontSize: 11.5, color: 'var(--text-muted)', textAlign: 'center', marginTop: 6, fontStyle: 'italic' },
};
